export interface AdPlatformConfig {
  name: string;
  icon: string;
  color: string;
  description: string;
  formats: string[];
  budgetTypes: string[];
  bidStrategies: string[];
  metrics: string[];
}

export const AD_PLATFORMS: Record<string, AdPlatformConfig> = {
  google: {
    name: 'Google Ads',
    icon: '🔍', // Using emoji for simplicity, can be replaced with SVG
    color: '#4285F4',
    description: 'Search, Display, Shopping, YouTube & Performance Max',
    formats: ['Search', 'Display', 'Shopping', 'Video', 'Performance Max', 'Discovery'],
    budgetTypes: ['Daily', 'Campaign Total', 'Monthly'],
    bidStrategies: [
      'Manual CPC',
      'Enhanced CPC',
      'Maximize Clicks',
      'Target CPA',
      'Target ROAS',
      'Maximize Conversions',
      'Maximize Conversion Value'
    ],
    metrics: ['Impressions', 'Clicks', 'CTR', 'CPC', 'Conversions', 'Cost', 'Conv. Rate']
  },
  facebook: {
    name: 'Facebook & Instagram',
    icon: '📱',
    color: '#1877F2',
    description: 'Feed, Stories, Reels, Messenger & Audience Network',
    formats: ['Image', 'Video', 'Carousel', 'Collection', 'Stories', 'Reels'],
    budgetTypes: ['Daily', 'Lifetime'],
    bidStrategies: [
      'Lowest Cost',
      'Cost Cap',
      'Bid Cap',
      'Target Cost',
      'Highest Value',
      'Minimum ROAS'
    ],
    metrics: ['Reach', 'Impressions', 'Clicks', 'CTR', 'CPM', 'CPC', 'Conversions', 'ROAS']
  },
  linkedin: {
    name: 'LinkedIn Ads',
    icon: '💼',
    color: '#0077B5',
    description: 'Sponsored Content, Message Ads, Dynamic Ads & Text Ads',
    formats: ['Sponsored Content', 'Message Ads', 'Dynamic Ads', 'Text Ads', 'Video Ads', 'Event Ads'],
    budgetTypes: ['Daily', 'Total', 'Lifetime'],
    bidStrategies: [
      'Maximum Delivery',
      'Cost Cap',
      'Manual Bidding',
      'Target Cost',
      'Enhanced CPC'
    ],
    metrics: ['Impressions', 'Clicks', 'CTR', 'Leads', 'Lead Form Opens', 'Cost per Lead', 'Engagement Rate']
  },
  display: {
    name: 'Display & Programmatic',
    icon: '🖼️',
    color: '#FF6B6B',
    description: 'Banner Ads, Native, Rich Media & Retargeting',
    formats: [
      '300x250 (Medium Rectangle)',
      '728x90 (Leaderboard)',
      '160x600 (Wide Skyscraper)',
      '336x280 (Large Rectangle)',
      '320x50 (Mobile Banner)',
      '970x250 (Billboard)',
      '300x600 (Half Page)',
      'Responsive',
      'Native',
      'HTML5'
    ],
    budgetTypes: ['Daily', 'Campaign Total', 'Monthly'],
    bidStrategies: [
      'CPM',
      'vCPM',
      'CPC',
      'CPA',
      'Dynamic CPM',
      'Header Bidding',
      'Private Marketplace',
      'Programmatic Guaranteed'
    ],
    metrics: ['Impressions', 'Viewable Impressions', 'Clicks', 'CTR', 'Conversions', 'View-through Conversions', 'CPM', 'vCPM']
  },
  ctv: {
    name: 'Connected TV',
    icon: '📺',
    color: '#9B59B6',
    description: 'Streaming TV, OTT Platforms & Smart TV Advertising',
    formats: [
      'Pre-roll (15s)',
      'Pre-roll (30s)',
      'Mid-roll (15s)',
      'Mid-roll (30s)',
      'Mid-roll (60s)',
      'Post-roll',
      'Interactive Overlay',
      'Pause Ads',
      'Home Screen Ads'
    ],
    budgetTypes: ['Campaign Total', 'Monthly', 'Quarterly'],
    bidStrategies: [
      'CPM',
      'CPCV (Cost per Completed View)',
      'Fixed Rate',
      'Programmatic Guaranteed',
      'Private Marketplace'
    ],
    metrics: ['Impressions', 'Completed Views', 'VTR (View-through Rate)', 'Reach', 'Frequency', 'CPCV', 'Brand Lift']
  },
  radio: {
    name: 'Radio & Audio',
    icon: '🎙️',
    color: '#1DB954',
    description: 'Spotify, Podcasts, Internet Radio & Terrestrial Radio',
    formats: [
      'Audio Spot (15s)',
      'Audio Spot (30s)',
      'Audio Spot (60s)',
      'Podcast Host-Read',
      'Podcast Pre-Produced',
      'Streaming Audio',
      'Terrestrial Radio',
      'Programmatic Audio'
    ],
    budgetTypes: ['Campaign Total', 'Weekly', 'Monthly'],
    bidStrategies: [
      'CPM',
      'Cost per Listen',
      'Fixed Sponsorship',
      'Dynamic Insertion',
      'Run of Schedule'
    ],
    metrics: ['Impressions', 'Listens', 'Listen-through Rate', 'Reach', 'Frequency', 'Share of Voice', 'Brand Recall']
  }
};

export type AdPlatformKey = keyof typeof AD_PLATFORMS;

// Common ad sizes for display advertising
export const COMMON_AD_SIZES = {
  display: {
    'Medium Rectangle': '300x250',
    'Leaderboard': '728x90',
    'Wide Skyscraper': '160x600',
    'Large Rectangle': '336x280',
    'Mobile Banner': '320x50',
    'Billboard': '970x250',
    'Half Page': '300x600',
    'Large Leaderboard': '970x90',
    'Portrait': '300x1050',
    'Mobile Interstitial': '320x480',
    'Square': '250x250'
  },
  social: {
    'Facebook Feed': '1200x628',
    'Instagram Feed': '1080x1080',
    'Instagram Story': '1080x1920',
    'LinkedIn Single Image': '1200x627',
    'Twitter Post': '1024x512'
  },
  video: {
    'Landscape (16:9)': '1920x1080',
    'Square (1:1)': '1080x1080',
    'Vertical (9:16)': '1080x1920',
    'Cinema (21:9)': '2560x1080'
  }
};

// Platform-specific requirements
export const PLATFORM_REQUIREMENTS = {
  google: {
    headlines: { min: 3, max: 15, charLimit: 30 },
    descriptions: { min: 2, max: 4, charLimit: 90 },
    path: { charLimit: 15 },
    keywords: { max: 20 }
  },
  facebook: {
    primaryText: { charLimit: 125 },
    headline: { charLimit: 40 },
    description: { charLimit: 30 },
    imageRatio: { min: 1.91, max: 1 }, // 1.91:1 to 1:1
    videoLength: { min: 1, max: 240 } // seconds
  },
  linkedin: {
    introText: { charLimit: 150 },
    headline: { charLimit: 70 },
    description: { charLimit: 100 }
  },
  display: {
    fileSize: { max: 150 }, // KB for standard display
    html5Size: { max: 5120 }, // KB for HTML5
    animationLength: { max: 30 } // seconds
  },
  ctv: {
    videoCodec: ['H.264', 'H.265'],
    audioCodec: ['AAC', 'MP3'],
    bitrate: { min: 10000, max: 50000 }, // kbps
    framerate: [23.976, 24, 25, 29.97, 30, 60]
  },
  radio: {
    audioFormat: ['MP3', 'WAV', 'AAC'],
    bitrate: { min: 128, max: 320 }, // kbps
    sampleRate: [44100, 48000] // Hz
  }
};